import { Fragment } from "react";
import { t } from "i18next";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPause } from "@fortawesome/pro-regular-svg-icons";

import { Empty } from "./Empty";
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import { Trash2Icon } from "lucide-react";

type BonosProps = {
  id: number | string;
  group: string;
  name: string;
  description: string;
  session: string;
  currency: string;
  price: string;
  days: string;
  state: string;
  status: number;
  groupbond: {
    name: string;
  };
};

export type TableListBonosProps = {
  bonos: BonosProps[];
  functionButtonMatriculas?: () => void;
  functionButtonPause?: (bondId: string, isEnable?: boolean) => void;
  functionButtonMotivos?: () => void;
  functionButtonEdit?: (bono: BonosProps) => void;
  functionButtonDelete?: (bono: BonosProps) => void;
  canEdit?: boolean;
  canPause?: boolean;
  canDelete?: boolean;
};

const TableListBonos: React.FC<TableListBonosProps> = ({
  bonos,
  functionButtonPause,
  functionButtonEdit,
  functionButtonDelete,
  canEdit,
  canPause,
  canDelete,
}) => {
  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-6">
      <div className="border-b border-gray-300 mt-5"></div>
      <div className="flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full px-3 py-2 align-middle sm:px-6 lg:px-8">
            {bonos.length === 0 ? (
              <Empty texto={t("slots.views.no_bonds")} />
            ) : (
              <Fragment>
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/6"
                      >
                        <div className="w-full">
                          {t("slots.views.group_table")}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/4"
                      >
                        <div className="w-full">{t("slots.views.name")}</div>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/6"
                      >
                        <div className="w-full">
                          {t("slots.views.description_edit")}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/6"
                      >
                        <div className="w-full">{t("slots.views.session")}</div>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/6"
                      >
                        <div className="w-full">{t("slots.views.price")}</div>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/6"
                      >
                        <div className="w-full">
                          {t("slots.views.days_caducity")}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/6"
                      >
                        <div className="w-full">{t("slots.views.state")}</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {bonos.map((bond) => (
                      <tr key={bond.id}>
                        <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-1/6">
                          <div className="w-full">
                            {bond?.groupbond?.name || ""}
                          </div>
                        </td>
                        <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-1/4">
                          <div className="w-full">{bond.name}</div>
                        </td>
                        <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-1/6">
                          <div className="w-full">{bond.description}</div>
                        </td>
                        <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-1/6">
                          <div className="w-full">{bond.session}</div>
                        </td>
                        <td className="px-0 py-3.5 text-left text-sm text-gray-900 w-1/6">
                          <div className="w-full">
                            {bond.price}
                            <span className="w-full"> {bond.currency}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-1/6">
                          <div className="w-full">{bond.days}</div>
                        </td>
                        <td className="px-3 py-3.5 text-left text-sm text-gray-900 w-1/6">
                          <div className="w-full">
                            {bond.status === 1
                              ? t("bond.views.active")
                              : t("bond.views.inactive")}
                          </div>
                        </td>
                        <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                          <div className="flex items-center justify-end">
                            {canPause && functionButtonPause && (
                              <a
                                onClick={() =>
                                  functionButtonPause(
                                    bond.id as string,
                                    bond.status === 1 ? true : false
                                  )
                                }
                                className="text-primary-600 cursor-pointer hover:text-primary-900"
                              >
                                <FontAwesomeIcon
                                  icon={bond.status === 1 ? faPause : faPlay}
                                  className="mr-4"
                                />
                              </a>
                            )}
                            {canEdit && functionButtonEdit && (
                              <button
                                className="text-primary-600 hover:text-primary-900 mr-2"
                                onClick={() => functionButtonEdit(bond)}
                              >
                                {t("general.edit")}
                                <span className="sr-only"></span>
                              </button>
                            )}
                            {functionButtonDelete && canDelete && (
                              <button
                                className="text-red-600 hover:text-red-900"
                                onClick={() => functionButtonDelete(bond)}
                              >
                                <Trash2Icon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableListBonos;
